import dayjs from 'dayjs'

import { apiClient } from '../infra/http/apiClient'
import { formatToDate } from '../utils/TimeUtility'

class PrivateSessionService {
  /**
   * Get all private sessions with optional filters
   * @param {Object} filters - Optional filters
   * @param {string} [filters.sport] - Filter by sport type (SNOWBOARD or SKI)
   * @param {string} [filters.instructorId] - Filter by instructor ID
   * @param {string} [filters.userId] - Filter by user ID
   * @param {string} [filters.status] - Filter by status
   * @param {number} [filters.dateAfter] - Filter sessions after this date (epoch timestamp)
   * @param {number} [filters.page=0] - Page number
   * @param {number} [filters.size=20] - Page size
   * @returns {Promise} Promise object represents the list of private sessions
   */
  static async getPrivateSessions(filters = {}) {
    try {
      const response = await apiClient.get('/private-sessions', {
        params: filters,
      })
      return response.data.map((privateSession) => ({
        ...privateSession,
        date: formatToDate(privateSession.startTime),
        timeSlot: `${dayjs(privateSession.startTime).format('HH:mm')} - ${dayjs(privateSession.endTime).format('HH:mm')}`,
      }))
    } catch (error) {
      console.error('Error fetching private sessions:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Get a specific private session by ID
   * @param {string} sessionId - The ID of the session to retrieve
   * @returns {Promise} Promise object represents the private session
   */
  static async getPrivateSession(sessionId) {
    try {
      const response = await apiClient.get(`/private-session/${sessionId}`)
      return response.data
    } catch (error) {
      console.error('Error fetching private session:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Create a new private session
   * @param {Object} sessionData - The session data
   * @param {string} sessionData.skiResort - Ski resort location
   * @param {string} sessionData.sport - Sport type (SNOWBOARD or SKI)
   * @param {string} sessionData.date - Session date (ISO format)
   * @param {string} sessionData.startTime - Start time (ISO format)
   * @param {string} sessionData.endTime - End time (ISO format)
   * @param {string} sessionData.instructorId - Instructor ID
   * @param {string} [sessionData.notes] - Optional notes
   * @returns {Promise} Promise object represents the created session
   */
  static async createPrivateSession(sessionData) {
    try {
      const response = await apiClient.post('/private-sessions', sessionData)
      return response.data
    } catch (error) {
      console.error('Error creating private session:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Update an existing private session
   * @param {string} sessionId - The ID of the session to update
   * @param {Object} updateData - The data to update
   * @returns {Promise} Promise object represents the updated session
   */
  static async updatePrivateSession(sessionId, updateData) {
    try {
      const response = await apiClient.put(`/private-session/${sessionId}`, updateData)
      return response.data
    } catch (error) {
      console.error('Error updating private session:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Delete a private session
   * @param {string} sessionId - The ID of the session to delete
   * @returns {Promise} Promise object represents the deletion result
   */
  static async deletePrivateSession(sessionId) {
    try {
      const response = await apiClient.delete(`/private-session/${sessionId}`)
      return response.data
    } catch (error) {
      console.error('Error deleting private session:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Register for a private session
   * @param {string} sessionId - The ID of the session to register for
   * @param {string[]} participantIds - Array of student IDs to register (max 4)
   * @param {Object[]} participants - Array of participant objects
   * @param {string} [notes] - Optional notes for the instructor
   * @returns {Promise} Promise object represents the registration result
   */
  static async registerSession(sessionId, participantIds = [], participants = [], notes = '') {
    try {
      const response = await apiClient.post(`/private-session/${sessionId}/register`, {
        participantIds,
        participants,
        notes,
      })
      return response.data
    } catch (error) {
      console.error('Error registering for session:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Cancel a private session registration
   * @param {string} sessionId - The ID of the session to cancel
   * @returns {Promise} Promise object represents the cancellation result
   */
  static async cancelSession(sessionId) {
    try {
      const response = await apiClient.post(`/private-session/${sessionId}/unregister`, { participantIds: [], participants: [] })
      return response.data
    } catch (error) {
      console.error('Error cancelling session:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Handle API errors and transform them into user-friendly messages
   * @private
   */
  static handleError(error) {
    if (error.response) {
      const { status, data } = error.response
      switch (status) {
        case 400:
          if (data === 'Past cancellation deadline' || data === 'Registration deadline has passed') {
            return new Error(data)
          }
          return new Error(data.message || 'Invalid request. Please check your input.')
        case 404:
          return new Error('Session not found.')
        case 409:
          return new Error(data.message || 'Session is no longer available.')
        case 401:
          return new Error('Please log in to continue.')
        case 403:
          return new Error('You do not have permission to perform this action.')
        case 503:
          if (data?.code === 'MAINTENANCE_MODE') {
            return new Error(data.message || 'The system is currently under maintenance. Please try again later.')
          }
          return new Error(data.message || 'Service temporarily unavailable. Please try again later.')
        default:
          return new Error('An unexpected error occurred. Please try again later.')
      }
    }
    // Network error or other issues
    return new Error('Unable to connect to the server. Please check your internet connection.')
  }
}

export default PrivateSessionService
